/**
 * IndividualPricingDialog - Dialog to set individual prices for a customer
 * Extracted from CustomersPage.js
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const IndividualPricingDialog = ({
  open,
  onOpenChange,
  services,
  pointPackages = [],
  subscriptionTemplates = [],
  editFormData,
  setEditFormData,
  onSavePrices
}) => {
  const creditPackages = (subscriptionTemplates || []).filter(template =>
    template.billing_cycle === 'once' || template.billing_cycle === 'one_time' || !template.billing_cycle
  );

  const recurringPackages = (subscriptionTemplates || []).filter(template =>
    template.billing_cycle === 'monthly' || template.billing_cycle === 'yearly' || template.billing_cycle === 'weekly'
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Individuelle Preise festlegen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <p className="text-sm text-blue-800">
              💡 <strong>Hinweis:</strong> Legen Sie spezielle Preise für diesen Kunden fest. Leer gelassene Services verwenden den Standard-Preis oder den generellen Rabatt.
            </p>
            <p className="text-xs text-blue-900">
              Für das <strong>Monats-Abo</strong> gilt:
              <br />
              • Der <strong>Initialbetrag</strong> wird sofort abgebucht (z.B. höherer Startbetrag) und gutgeschrieben, sobald Payrexx die Zahlung bestätigt.
              <br />
              • Ab dann wird der <strong>Monatsbetrag</strong> jeweils automatisch am <strong>25.</strong> belastet und die Monats-Punkte werden nach erfolgreicher Abbuchung gutgeschrieben.
            </p>
          </div>

          {/* Genereller Rabatt auf ALLE Dienstleistungen */}
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-4" data-testid="global-discount-box">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h4 className="font-semibold text-amber-900">Rabatt auf alle Dienstleistungen</h4>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  Gilt <strong>dauerhaft</strong> auf alle Services ohne eigenen Einzelpreis. Beim Speichern werden
                  automatisch alle offenen Termine (inkl. Serien) und offenen Rechnungen im Hintergrund neu berechnet.
                  Ein Einzelpreis oben hat Vorrang vor diesem Rabatt.
                </p>
              </div>
              <div className="w-28 shrink-0">
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="z.B. 4.6"
                    value={editFormData.discount_percent ?? ''}
                    onChange={(e) => {
                      let v = e.target.value.replace(',', '.');
                      if (!/^\d*\.?\d*$/.test(v)) return;
                      setEditFormData((prev) => ({ ...prev, discount_percent: v }));
                    }}
                    className="pr-7 text-right font-semibold"
                    data-testid="global-discount-percent-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
              </div>
            </div>
            {editFormData.discount_percent > 0 && (
              <p className="text-sm text-emerald-700 font-semibold mt-2" data-testid="global-discount-hint">
                {editFormData.discount_percent}% Rabatt aktiv — ein Service für CHF 100 kostet dann CHF {(100 * (1 - editFormData.discount_percent / 100)).toFixed(2)}.
              </p>
            )}
          </div>

          {/* Rabatt auf ALLE Punkte-Käufe */}
          <div className="border-2 border-sky-300 bg-sky-50 rounded-lg p-4" data-testid="points-discount-box">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h4 className="font-semibold text-sky-900">Rabatt auf alle Punkte-Käufe</h4>
                <p className="text-xs text-sky-800 mt-1 leading-relaxed">
                  Gilt <strong>dauerhaft</strong> auf alle Punkte-Käufe (freie Punkteanzahl & fixe Punkte-Pakete)
                  in Verkaufen, Portal und App. Beim Zusammentreffen mit dem automatischen Mengenrabatt
                  (Staffel-Bonus) gilt der <strong>höhere</strong> Rabatt (kein Stapeln).
                </p>
              </div>
              <div className="w-28 shrink-0">
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="z.B. 4.6"
                    value={editFormData.points_discount_percent ?? ''}
                    onChange={(e) => {
                      let v = e.target.value.replace(',', '.');
                      if (!/^\d*\.?\d*$/.test(v)) return;
                      setEditFormData((prev) => ({ ...prev, points_discount_percent: v }));
                    }}
                    className="pr-7 text-right font-semibold"
                    data-testid="points-discount-percent-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
              </div>
            </div>
            {editFormData.points_discount_percent > 0 && (
              <p className="text-sm text-emerald-700 font-semibold mt-2" data-testid="points-discount-hint">
                {editFormData.points_discount_percent}% Rabatt auf Punkte aktiv.
              </p>
            )}
          </div>

          {/* Rabatt auf ALLE Service-Abos */}
          <div className="border-2 border-violet-300 bg-violet-50 rounded-lg p-4" data-testid="abo-discount-box">
            <div className="flex justify-between items-start gap-4">
              <div>
                <h4 className="font-semibold text-violet-900">Rabatt auf alle Service-Abos</h4>
                <p className="text-xs text-violet-800 mt-1 leading-relaxed">
                  Gilt <strong>dauerhaft</strong> auf alle Service-Abos (10er/20er-Abos & Service-Kontingente).
                  Beim Zusammentreffen mit dem Mengenrabatt gilt der <strong>höhere</strong> Rabatt (kein Stapeln).
                </p>
              </div>
              <div className="w-28 shrink-0">
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="z.B. 4.6"
                    value={editFormData.service_abo_discount_percent ?? ''}
                    onChange={(e) => {
                      let v = e.target.value.replace(',', '.');
                      if (!/^\d*\.?\d*$/.test(v)) return;
                      setEditFormData((prev) => ({ ...prev, service_abo_discount_percent: v }));
                    }}
                    className="pr-7 text-right font-semibold"
                    data-testid="abo-discount-percent-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                </div>
              </div>
            </div>
            {editFormData.service_abo_discount_percent > 0 && (
              <p className="text-sm text-emerald-700 font-semibold mt-2" data-testid="abo-discount-hint">
                {editFormData.service_abo_discount_percent}% Rabatt auf Service-Abos aktiv.
              </p>
            )}
          </div>

          <div className="space-y-3">
            {services.map((service) => {
              const currentPrice = editFormData.individual_prices?.[service.id] || '';
              const standardPrice = service.price || 0;
              
              return (
                <div key={service.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold">{service.name}</h4>
                      <p className="text-sm text-gray-600">Standard: CHF {standardPrice.toFixed(2)}</p>
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={standardPrice.toFixed(2)}
                        value={currentPrice}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEditFormData(prev => ({
                            ...prev,
                            individual_prices: {
                              ...prev.individual_prices,
                              [service.id]: value ? parseFloat(value) : undefined
                            }
                          }));
                        }}
                        data-testid={`individual-price-service-${service.id}`}
                      />
                    </div>
                  </div>
                  {currentPrice && (
                    <div className="text-sm">
                      <span className="text-green-600 font-semibold">
                        Ersparnis: CHF {(standardPrice - parseFloat(currentPrice)).toFixed(2)} 
                        ({(((standardPrice - parseFloat(currentPrice)) / standardPrice) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {pointPackages.length > 0 && (
            <div className="space-y-3" data-testid="individual-pricing-point-packages">
              <h4 className="text-sm font-semibold text-gray-700">Punkte-Pakete</h4>
              {pointPackages.map((pkg) => {
                const currentPrice = editFormData.individual_package_prices?.[pkg.id] || '';
                const standardPrice = pkg.price || 0;

                return (
                  <div key={pkg.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold">{pkg.name}</h4>
                        <p className="text-sm text-gray-600">Standard: CHF {standardPrice.toFixed(2)}</p>
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={standardPrice.toFixed(2)}
                          value={currentPrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEditFormData(prev => ({
                              ...prev,
                              individual_package_prices: {
                                ...prev.individual_package_prices,
                                [pkg.id]: value ? parseFloat(value) : undefined
                              }
                            }));
                          }}
                          data-testid={`individual-price-package-${pkg.id}`}
                        />
                      </div>
                    </div>
                    {currentPrice && (
                      <div className="text-sm">
                        <span className="text-green-600 font-semibold">
                          Ersparnis: CHF {(standardPrice - parseFloat(currentPrice)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {creditPackages.length > 0 && (
            <div className="space-y-3" data-testid="individual-pricing-credit-packages">
              <h4 className="text-sm font-semibold text-gray-700">Abos (10er/Einmalpakete)</h4>
              {creditPackages.map((pkg) => {
                const currentPrice = editFormData.individual_subscription_prices?.[pkg.id] || '';
                const standardPrice = pkg.price || 0;

                return (
                  <div key={pkg.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold">{pkg.name}</h4>
                        <p className="text-sm text-gray-600">Standard: CHF {standardPrice.toFixed(2)}</p>
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={standardPrice.toFixed(2)}
                          value={currentPrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEditFormData(prev => ({
                              ...prev,
                              individual_subscription_prices: {
                                ...prev.individual_subscription_prices,
                                [pkg.id]: value ? parseFloat(value) : undefined
                              }
                            }));
                          }}
                          data-testid={`individual-price-subscription-${pkg.id}`}
                        />
                      </div>
                    </div>
                    {currentPrice && (
                      <div className="text-sm">
                        <span className="text-green-600 font-semibold">
                          Ersparnis: CHF {(standardPrice - parseFloat(currentPrice)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {recurringPackages.length > 0 && (
            <div className="space-y-3" data-testid="individual-pricing-recurring-packages">
              <h4 className="text-sm font-semibold text-gray-700">Wiederkehrende Abos</h4>
              {recurringPackages.map((pkg) => {
                const currentPrice = editFormData.individual_subscription_prices?.[pkg.id] || '';
                const standardPrice = pkg.price || 0;

                return (
                  <div key={pkg.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold">{pkg.name}</h4>
                        <p className="text-sm text-gray-600">Standard: CHF {standardPrice.toFixed(2)}</p>
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={standardPrice.toFixed(2)}
                          value={currentPrice}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEditFormData(prev => ({
                              ...prev,
                              individual_subscription_prices: {
                                ...prev.individual_subscription_prices,
                                [pkg.id]: value ? parseFloat(value) : undefined
                              }
                            }));
                          }}
                          data-testid={`individual-price-recurring-${pkg.id}`}
                        />
                      </div>
                    </div>
                    {currentPrice && (
                      <div className="text-sm">
                        <span className="text-green-600 font-semibold">
                          Ersparnis: CHF {(standardPrice - parseFloat(currentPrice)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={onSavePrices} data-testid="save-individual-prices-btn">
              Preise speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IndividualPricingDialog;
